import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useRouter } from "next/navigation";
import { format } from "path";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { ReplaySubject } from "rxjs";
import { erc20Abi, parseUnits } from "viem";
import { formatUnits } from "viem";
import {
  getGasPrice,
  sendTransaction,
  waitForTransactionReceipt,
} from "viem/actions";
import {
  useAccount,
  useBlockNumber,
  useChainId,
  useReadContract,
  useSimulateContract,
  useWalletClient,
  useWriteContract,
} from "wagmi";

import { isPendleSwapResponse } from "@/app/api/swap/pendle/is-pendle-swap-response";
import { getFarmTokenSymbol } from "@/app/util";
import { SUPPORTED_CHAINS } from "@/constants/chains";
import { NULL_ADDRESS } from "@/constants/common";
import { CallType } from "@/constants/enum";
import { TEN, ZERO } from "@/constants/math";
import { useRx } from "@/contexts/rx";
import { useWeb3Context } from "@/contexts/web3";
import { getDefaultCalldataSelector } from "@/contracts/selectors/marginly-pool/v15";
import { getExecuteParams } from "@/contracts/tx/execute";
import { useMarginlyApi } from "@/hooks/api";
import { useApprove } from "@/hooks/approve";
import { useDebounce } from "@/hooks/common";
import { useDisplayError } from "@/hooks/error";
import { useGas } from "@/hooks/gas";
import { usePoolCoeffs, usePoolData } from "@/hooks/position";
import { useApply } from "@/hooks/util";
import { useTokenBalance } from "@/hooks/wagmi/use-token-balance";
import { isFiniteNumber, isValidInputAmount } from "@/util/check";
import { getLiquidationDate } from "@/util/get-day-difference";

import { useFarmRepresentation } from "../../hooks/use-farm-representation";
import { ZERO_ADDRESS } from "./constants/constants";
import { getFarmState } from "./constants/farm-state/get-farm-state";
import { getFarmingPositionParams } from "./util";

const PENDLE_SLIPPAGE = 0.002;
const PRICE_IMPACT_THRESHOLD = 0.01;

type State = {
  step: "swap" | "farm";
  selectedToken: number;
  swapSpender: `0x${string}` | undefined;
  swapCalldata: `0x${string}` | undefined;
  pending: boolean;
  approved: boolean;
  priceImpact: number | undefined;
  isFlowActive: boolean;
  swapAmountPt: string | undefined;
};

type Action =
  | { type: "switchToSwap" }
  | { type: "switchToFarm" }
  | {
      type: "selectToken";
      selectedToken: number;
    }
  | {
      type: "setSwapData";
      swapSpender: `0x${string}`;
      swapCalldata: `0x${string}`;
      swapAmountPt: string;
    }
  | { type: "resetSwapData" }
  | { type: "startPending" }
  | { type: "stopPending" }
  | { type: "setApproved" }
  | { type: "resetApproved" }
  | { type: "setPriceImpact"; priceImpact: number }
  | { type: "startFlow" }
  | { type: "stopFlow" };

const initialState: State = {
  step: "swap",
  selectedToken: 0,
  swapCalldata: undefined,
  swapSpender: undefined,
  pending: false,
  approved: false,
  priceImpact: undefined,
  isFlowActive: false,
  swapAmountPt: undefined,
};

const logError = (e$: ReplaySubject<Error>) => (e: unknown) => {
  if (!(e instanceof Error)) {
    return;
  }

  console.error(e);
  // TODO: enable error messages for user. Mb add some filter?
  // e$.next(e);
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "selectToken":
      return {
        ...state,
        selectedToken: action.selectedToken,
      };
    case "switchToFarm":
      return {
        ...state,
        step: "farm",
      };
    case "switchToSwap":
      return {
        ...state,
        step: "swap",
      };
    case "resetSwapData": {
      return {
        ...state,
        swapCalldata: undefined,
        swapSpender: undefined,
        swapAmountPt: undefined,
      };
    }
    case "setSwapData": {
      return {
        ...state,
        swapSpender: action.swapSpender,
        swapCalldata: action.swapCalldata,
        swapAmountPt: action.swapAmountPt,
      };
    }
    case "startPending": {
      return {
        ...state,
        pending: true,
      };
    }
    case "stopPending": {
      return {
        ...state,
        pending: false,
      };
    }
    case "setApproved": {
      return {
        ...state,
        approved: true,
      };
    }
    case "setPriceImpact": {
      return {
        ...state,
        priceImpact: action.priceImpact,
      };
    }
    case "startFlow": {
      return {
        ...state,
        isFlowActive: true,
      };
    }
    case "stopFlow": {
      return {
        ...state,
        isFlowActive: false,
      };
    }
    default:
      return state;
  }
};

export type RiskLevel = "low" | "medium" | "hight";

export const leverageByRiskLevel: Record<RiskLevel, number> = {
  low: 2,
  medium: 5,
  hight: 10,
};

const MAX_LEVERAGE = 20;

export const useFarm = ({
  poolAddress,
  chainName,
  days,
  onClose,
  maturityDate,
}: {
  poolAddress: `0x${string}`;
  chainName: string;
  days: number | null;
  onClose: () => void;
  maturityDate?: string;
}) => {
  const maturityDateFormatted =
    maturityDate &&
    new Date(maturityDate).toLocaleDateString("en-US", {
      weekday: undefined,
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const liquidationDate = maturityDate
    ? getLiquidationDate(maturityDate)
    : undefined;

  const refetchRef = useRef<() => Promise<void>>();
  const { getPoolByAddress, internalChainId } = useWeb3Context();

  const [riskLevel, setRiskLevel] = useState<RiskLevel>("hight");

  const leverage = leverageByRiskLevel[riskLevel];

  const lowApr = useMarginlyApi({
    endpoint: "poolApr",
    args: [internalChainId, poolAddress, leverageByRiskLevel["low"]],
  });

  const mediumApr = useMarginlyApi({
    endpoint: "poolApr",
    args: [internalChainId, poolAddress, leverageByRiskLevel["medium"]],
  });

  const hightApr = useMarginlyApi({
    endpoint: "poolApr",
    args: [internalChainId, poolAddress, leverageByRiskLevel["hight"]],
  });

  const apyByRiskLevel: Record<RiskLevel, number> = {
    low: lowApr.data?.farmingBaseApy || 0,
    medium: mediumApr.data?.farmingBaseApy || 0,
    hight: hightApr.data?.farmingBaseApy || 0,
  };

  const payAprByRiskLevel: Record<RiskLevel, number> = {
    low: lowApr.data?.borrowCurrentQuoteApr || 0,
    medium: mediumApr.data?.borrowCurrentQuoteApr || 0,
    hight: hightApr.data?.borrowCurrentQuoteApr || 0,
  };

  const chainId = useChainId();
  const coeffs = usePoolCoeffs(poolAddress);
  const { address } = useAccount();
  const router = useRouter();
  // const poolParams = usePoolParams(poolAddress);
  const poolData = usePoolData(poolAddress);
  const positionParams = useApply(poolData.getPositionParams, coeffs);
  const { error$ } = useRx();
  // const position = usePositionsInfo(poolAddress, { disableLiqPrice: true });
  const { data: blockNumber } = useBlockNumber();
  const { data: walletClient } = useWalletClient();
  const { base, basePrice, quote, uniswapAddress } = usePoolData(poolAddress);

  const poolInfo = getPoolByAddress(poolAddress)?.pool;
  const sparksPerQuotePerDay = poolInfo?.sparksPerQuotePerDay;

  const isPendle = poolInfo?.isPendle;
  const { data: defaultCallData } = useReadContract({
    ...getDefaultCalldataSelector(poolAddress),
  });

  const { farmRepresentation } = useFarmRepresentation(poolAddress);
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    if (poolInfo && !isPendle) {
      dispatch({ type: "switchToFarm" });
    }
  }, [isPendle, poolInfo]);

  const { data: baseBalance } = useTokenBalance({
    address,
    token: base?.address,
  });

  const [inputAmount, setInputAmount] = useState("");
  const [debouncedAmount, isDebounce] = useDebounce(inputAmount, {
    delay: 1000,
  });
  const [gasPrice, setGasPrice] = useState<bigint>();

  const poolState = getFarmState(chainId, poolAddress);
  const tokens = poolState?.tokens;
  const selectedToken = tokens?.[state.selectedToken];

  const { data: balance } = useTokenBalance({
    address,
    token: selectedToken?.address,
  });

  const payApr = payAprByRiskLevel[riskLevel] / 100;
  const earnApy = payApr + apyByRiskLevel[riskLevel] / 100;

  const inputAmountInPt =
    (state.swapAmountPt &&
      baseBalance?.decimals &&
      formatUnits(BigInt(state.swapAmountPt), baseBalance.decimals)) ||
    "";

  const depositAmount = state.step === "farm" ? inputAmount : inputAmountInPt;
  const price = farmRepresentation?.isRepresentationSameAsUnderlying
    ? basePrice
    : BigNumber(farmRepresentation?.lastPtPrice || 1);

  const depositAmountInQuote = price.times(depositAmount || 0);

  const { debtAtMaturity, netPositionAtMaturity, collateral, debt } =
    getFarmingPositionParams({
      basePrice: price,
      leverage,
      maturityInDays: days,
      depositAmount,
      payApr,
    });

  const pendleRate = BigNumber(inputAmount).div(BigNumber(inputAmountInPt));

  const netPositionAtMaturityInBase =
    (netPositionAtMaturity || 0) / price.toNumber();

  const receiveSum =
    state.step === "farm"
      ? netPositionAtMaturityInBase
      : netPositionAtMaturityInBase * pendleRate.toNumber();

  const liquidationPrice =
    collateral.toNumber() &&
    ((debt.toNumber() / collateral.toNumber()) * MAX_LEVERAGE) /
      (MAX_LEVERAGE - 1);

  const lentAmount = positionParams?.realBaseAmount
    ?.times(basePrice)
    .plus(positionParams?.realQuoteAmount ?? 0);

  const isPriceImpactHigh =
    state.priceImpact && state.priceImpact > PRICE_IMPACT_THRESHOLD;

  const approve = useApprove({
    token:
      selectedToken?.needApprove && isValidInputAmount(inputAmount)
        ? selectedToken?.address
        : undefined,
    spender: state.step === "farm" ? poolAddress : state.swapSpender,
    amount:
      balance && isValidInputAmount(debouncedAmount)
        ? ZERO.plus(debouncedAmount)
            .minus(lentAmount ?? 0)
            .toString()
        : undefined,
    refetch: refetchRef.current,
    oneStepSubmit: true,
  });

  const min = useMemo(() => {
    if (!positionParams?.basePrice || state.step !== "farm") return undefined;
    return undefined;
    // return lentAmount?.div(basePrice);
  }, [positionParams?.basePrice, state.step]);

  const max = useMemo(() => {
    if (state.step === "swap" && balance) {
      return new BigNumber(balance.formatted);
    }
    if (positionParams?.basePrice && balance && state.step === "farm" && base) {
      const am = lentAmount?.div(basePrice).plus(balance?.formatted);

      return am?.dp(base?.decimals);
    }
  }, [
    balance,
    base,
    basePrice,
    lentAmount,
    positionParams?.basePrice,
    state.step,
  ]);

  const notEnoughFunds = useMemo(() => {
    if (!inputAmount || !isValidInputAmount(inputAmount)) {
      return false;
    }

    if (!balance) {
      return false;
    }

    const bAmount = ZERO.plus(inputAmount)
      .minus(lentAmount ?? 0)
      .times(TEN.pow(balance.decimals))
      .dp(0);

    return !bAmount.lte(balance.value.toString(10));
  }, [balance, inputAmount, lentAmount]);

  const notEnoughToOpenPosition = useMemo(() => {
    if (!inputAmount || !min || !isValidInputAmount(inputAmount)) {
      return false;
    }

    return ZERO.plus(inputAmount).lt(min);
  }, [inputAmount, min]);

  const farmArgs = useMemo(() => {
    if (
      state.step !== "farm" ||
      approve.needApprove ||
      !balance ||
      !base ||
      !quote ||
      !isValidInputAmount(debouncedAmount) ||
      isDebounce
    )
      return undefined;

    const leveraged = ZERO.plus(debouncedAmount)
      .times((leverage || 0) - 1)
      .toString();

    const args: [
      number,
      bigint,
      bigint,
      boolean,
      "0x0000000000000000000000000000000000000000",
    ] = [
      CallType.DepositBase,
      parseUnits(debouncedAmount, balance.decimals),
      parseUnits(leveraged, balance.decimals),
      false, // unwrap is always false
      NULL_ADDRESS,
    ];
    return {
      ...getExecuteParams(args, {
        basePrice,
        baseDecimals: base.decimals,
        quoteDecimals: quote.decimals,
        swapCallData: (defaultCallData as number | undefined)?.toString(10),
        version: "v15",
      }),
      address: poolAddress,
      value: selectedToken?.address
        ? 0n
        : parseUnits(debouncedAmount, balance.decimals),
    };
  }, [
    approve.needApprove,
    balance,
    base,
    basePrice,
    debouncedAmount,
    defaultCallData,
    isDebounce,
    poolAddress,
    leverage,
    quote,
    selectedToken?.address,
    state.step,
  ]);

  const withdrawGas = useGas();
  // @ts-ignore
  const prepare = useSimulateContract(farmArgs);
  withdrawGas.useError(prepare.error);
  const { lastError } = useDisplayError(prepare.refetch);

  const isButtonDisabled =
    !isValidInputAmount(inputAmount) ||
    notEnoughFunds ||
    notEnoughToOpenPosition ||
    state.pending ||
    isDebounce ||
    (state.step === "swap" && state.swapCalldata === undefined) ||
    (state.step === "farm" && prepare.isPending && !approve.needApprove);

  const farmTx = useWriteContract();
  // to get gas price
  useEffect(() => {
    if (!walletClient) return;

    getGasPrice(walletClient).then(setGasPrice).catch(logError(error$));
  }, [error$, walletClient, blockNumber]);

  // to get pendle swap data
  useEffect(() => {
    if (state.step !== "swap" || isDebounce) return;
    if (!chainId || !balance?.decimals || !address || !isPendle) return;

    dispatch({ type: "resetSwapData" }); // reset if any input change happens

    if (!isValidInputAmount(debouncedAmount)) return;

    fetch(
      `/api/swap/pendle?chainId=${chainId}&receiverAddr=${address}&marketAddr=${uniswapAddress}&tokenInAddr=${
        selectedToken?.address ?? ZERO_ADDRESS
      }&amountTokenIn=${TEN.pow(balance.decimals)
        .times(debouncedAmount)
        .dp(0)
        .toString()}&slippage=${PENDLE_SLIPPAGE}`,
    )
      .then((res) => res.json())
      .then((json) => {
        if (!isPendleSwapResponse(json)) {
          throw new Error("Pendle swap response validation failed");
        }
        dispatch({
          type: "setSwapData",
          swapSpender: json.transaction.to,
          swapCalldata: json.transaction.data,
          swapAmountPt: json.data.amountPtOut,
        });
      })
      .catch(logError(error$));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    balance?.decimals,
    address,
    chainId,
    debouncedAmount,
    error$,
    isDebounce,
    selectedToken?.address,
    state.step,
    uniswapAddress,
  ]);

  // to get price impact
  useEffect(() => {
    if (!chainId || !address || isDebounce || !isPendle) return;

    if (!isValidInputAmount(debouncedAmount)) return;

    fetch(
      `/api/swap/pendle?chainId=${chainId}&receiverAddr=${address}&marketAddr=${uniswapAddress}&tokenInAddr=${
        poolState?.priceImpactToken?.address ?? ZERO_ADDRESS
      }&amountTokenIn=${TEN.pow(poolState?.priceImpactToken?.decimals || 0)
        .times(debouncedAmount)
        .times(leverage || 0)
        .dp(0)
        .toString()}&slippage=${PENDLE_SLIPPAGE}`,
    )
      .then((res) => res.json())
      .then((json) => {
        if (!isPendleSwapResponse(json)) {
          throw new Error("Pendle swap response validation failed");
        }

        dispatch({
          type: "setPriceImpact",
          priceImpact: json.data.priceImpact,
        });
      })
      .catch(logError(error$));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    balance?.decimals,
    chainId,
    debouncedAmount,
    error$,
    isDebounce,
    leverage,
    poolState?.priceImpactToken?.address,
    poolState?.priceImpactToken?.decimals,
    selectedToken?.address,
    state.step,
    uniswapAddress,
  ]);

  const flowModalStep = useMemo(() => {
    if (!state.isFlowActive) return undefined;

    if (state.step === "swap")
      return approve.needApprove ? "approveSwap" : "makeSwap";

    if (state.step === "farm")
      return approve.needApprove ? "approveFarm" : "makeFarm";
  }, [approve.needApprove, state.isFlowActive, state.step]);

  const handleInputChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >((e) => {
    if (isFiniteNumber(+e.target.value)) {
      setInputAmount(e.target.value);
    }
  }, []);

  const handleMinClick = useCallback(() => {
    if (!min) return;

    // setInputAmount(min.toString());
  }, [min]);

  const handleMaxClick = useCallback(() => {
    if (!max) return;

    setInputAmount(max.toString());
  }, [max]);

  const handleSelectToken = useCallback(
    (selectedToken: number) => {
      if (!tokens) return;

      if (
        tokens[selectedToken]?.shouldBeSwapped &&
        !tokens[state.selectedToken]?.shouldBeSwapped
      ) {
        dispatch({ type: "switchToSwap" });
      }
      if (
        !tokens[selectedToken]?.shouldBeSwapped &&
        tokens[state.selectedToken]?.shouldBeSwapped
      ) {
        dispatch({ type: "switchToFarm" });
      }
      dispatch({ type: "selectToken", selectedToken });
    },
    [state.selectedToken, tokens],
  );
  const handleFarmClick = useCallback(() => {
    if (approve.needApprove) {
      dispatch({ type: "startFlow" });
      return;
    }
    if (prepare.error) {
      lastError();
      return;
    }
    if (state.step === "farm" && !prepare.data) {
      return;
    }
    dispatch({ type: "startFlow" });
  }, [approve.needApprove, lastError, prepare.data, prepare.error, state.step]);

  const handleCloseClick = useCallback(() => {
    if (state.isFlowActive) {
      dispatch({ type: "stopFlow" });
      return;
    }
    onClose();
  }, [onClose, state.isFlowActive]);

  const handleSubmit = useCallback(async () => {
    if (approve.needApprove && walletClient && approve.submit && approve) {
      try {
        dispatch({ type: "startPending" });
        const receipt = await approve.submit();
        if (!receipt) {
          logError(error$)(new Error("Approve failed"));
          return;
        }

        await waitForTransactionReceipt(walletClient, {
          hash: receipt.transactionHash,
        });
        dispatch({ type: "setApproved" });
      } catch (e) {
        logError(error$)(new Error("Approve failed"));
      } finally {
        dispatch({ type: "stopPending" });
      }
      return;
    }
    if (
      state.step === "swap" &&
      state.swapCalldata &&
      state.swapSpender &&
      walletClient &&
      balance
    ) {
      const chain =
        SUPPORTED_CHAINS[chainName as keyof typeof SUPPORTED_CHAINS];

      if (!chain) return;

      try {
        dispatch({ type: "startPending" });
        const hash = await sendTransaction(walletClient, {
          // chain,
          account: address,
          data: state.swapCalldata,
          to: state.swapSpender,
          value: selectedToken?.address
            ? 0n
            : BigInt(
                TEN.pow(balance.decimals)
                  .times(debouncedAmount)
                  .dp(0)
                  .toString(),
              ),
        });
        if (!hash) {
          logError(error$)(new Error("Swap failed"));
          return;
        }

        await waitForTransactionReceipt(walletClient, {
          hash,
        });
        const farmTokenIdx = tokens?.findIndex((x) => !x.shouldBeSwapped);
        handleSelectToken(farmTokenIdx || 0);
        const newInputValue =
          state.swapAmountPt &&
          baseBalance?.decimals &&
          formatUnits(BigInt(state.swapAmountPt), baseBalance.decimals);
        newInputValue && setInputAmount(newInputValue);
      } catch (e) {
        logError(error$)(new Error("Swap failed"));
      } finally {
        dispatch({ type: "stopPending" });
      }
      return;
    }
    if (state.step === "farm") {
      if (prepare.error) {
        lastError();
        return;
      }
      if (prepare.data?.request && walletClient) {
        try {
          dispatch({ type: "startPending" });
          dispatch({ type: "resetApproved" });
          const result = await farmTx.writeContractAsync(prepare.data.request);
          if (!result) {
            logError(error$)(new Error("Swap failed"));
            return;
          }

          await waitForTransactionReceipt(walletClient, {
            hash: result,
          });
          router.push(
            `/${chainName}/farm-success?${
              days ? `days=${days}&` : ""
            }token=${getFarmTokenSymbol(
              selectedToken?.symbol,
            )}&amount=${inputAmount}${
              maturityDateFormatted
                ? `&maturityDateFormatted=${maturityDateFormatted}`
                : ""
            }&poolAddress=${poolAddress}`,
          );
        } catch (e) {
          logError(error$)(new Error("Farm failed"));
        } finally {
          dispatch({ type: "stopPending" });
        }
        return;
      }
    }
  }, [
    address,
    approve,
    balance,
    baseBalance?.decimals,
    chainName,
    days,
    debouncedAmount,
    error$,
    farmTx,
    handleSelectToken,
    inputAmount,
    lastError,
    maturityDateFormatted,
    poolAddress,
    prepare.data?.request,
    prepare.error,
    router,
    selectedToken?.address,
    selectedToken?.symbol,
    state.step,
    state.swapAmountPt,
    state.swapCalldata,
    state.swapSpender,
    tokens,
    walletClient,
  ]);

  return {
    earnApy,
    base,
    basePrice,
    collateral,
    debt,
    debtAtMaturity,
    handleCloseClick,
    handleFarmClick,
    handleInputChange,
    handleMaxClick,
    handleMinClick,
    handleSelectToken,
    handleSubmit,
    inputAmount,
    payApr,
    isButtonDisabled,
    isPriceImpactHigh,
    leverage,
    max,
    min,
    netPositionAtMaturity,
    notEnoughFunds,
    notEnoughToOpenPosition,
    priceImpact: state.priceImpact,
    priceWithImpact: basePrice.times(1 + (state.priceImpact ?? 0)),
    quote,
    selectedToken,
    sparksPerQuotePerDay,
    tokens,
    flowModalStep,
    pending: state.pending,
    setRiskLevel,
    riskLevel,
    apyByRiskLevel,
    depositAmountInQuote,
    liquidationPrice,
    isPendle,
    liquidationDate,
    receiveSum,
  };
};
